import {observer} from 'mobx-react';
import momentTimezone from 'moment-timezone';
import qs from 'qs';
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {Link, useParams} from 'react-router-dom';
import {Container} from 'reactstrap';
import styled from 'styled-components';
import {from, fromEvent} from 'rxjs';
import {debounceTime, share} from 'rxjs/operators';
import {ThemeContext} from '../../apiContexts';
import InputSearchWithRef from '../../components/inputSearch/inputSearchWithRef';
import LoadingBar from '../../components/loadingBar';
import ReactTable from '../../components/reactTable';
import ReactExport from 'react-export-excel';
import RoundButton from '../../components/roundButton';
import {
  activeLabelsArray,
  buildGroupQuery,
  communicationLabelsArray,
  communicationTime,
  createLabelIcon,
  getDeviceActiveGroupIdByIndex,
  getCurrentCategories,
  getDateGroup,
  getDeviceActiveGroup,
  getGroupByCategoryStatus,
  normalizeAdminChartGroup,
  getSinisterGroup,
  getStatusGroup,
  sinisterDurationLabelsArray,
  sinisterGroupIds,
  stateGroupIds,
  statusLabelsArray,
} from '../../helpers/admin';
import {getOffsetTo} from '../../helpers/DOM';
import {formatDateWithPattern} from '../../helpers/excel-helper';
import {measureTextWidth} from '../../helpers/textMeasure';
import {sampleWidest} from '../../helpers/columnWidth';
import useStore from '../../hooks/use-store';
import history from '../../routes/history';
import {notificationClickState} from '../../helpers/notificationClick';
import {ReactSelectStyled} from '../../styles';
import {Header, ListStyle} from '../../views/reports/styles';

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

// ReactTable (src/components/reactTable) é compartilhado com as 7 telas de resultado de
// relatório -- centralizar o título lá afetaria as duas juntas. Escopo só pra esta tela: a classe
// do wrapper vence a regra global (mais específica, sem precisar de !important) e não mexe no
// componente nem no style.js compartilhado. Linha (.td) fica no alinhamento padrão (esquerda,
// herdado de style.js) -- só o título é centralizado.
const ChartTableThemedHeader = styled.div`
  .header .th {
    text-align: center;
  }

  // Largura calculada (getMeasuredColumnWidth, abaixo) já mede o texto exato -- nowrap+ellipsis é
  // só rede de segurança pro caso raro em que outro valor da lista, mais largo que o medido
  // nesta amostra, apareça ao rolar. Sem isso quebraria linha em vez de cortar com reticência.
  .header .th,
  .body .td {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

// Largura por pixel real (canvas), não char×10px -- helper compartilhado com helpers/columnWidth.js
// (DataTable/sinister). Local a esta tela só o font stack e o padding de célula.
const CHART_CELL_FONT_STACK = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

// Padding real da célula (.td padding-left: 1.25rem = 20px, style.js) + folga de borda/direita.
const CHART_CELL_PADDING_PX = 42;

// `pick` devolve o valor barato de cada linha (texto ou data crua) e `format` o transforma no que a
// celula mostra; so a amostra dos mais longos (sampleWidest) passa por `format`.
export const getMeasuredColumnWidth = (data, pick, headerText, format) => {
  const cellFont = `400 14.4px ${CHART_CELL_FONT_STACK}`;
  const headerFont = `700 16px ${CHART_CELL_FONT_STACK}`;
  const rowPick = typeof pick === 'string' ? (row) => row[pick] : pick;
  const widestPx = sampleWidest(data, rowPick, format).reduce(
      (max, value) => Math.max(max, measureTextWidth(String(value ?? ''), cellFont)),
      measureTextWidth(headerText, headerFont),
  );
  return Math.ceil(widestPx) + CHART_CELL_PADDING_PX;
};

const AdminDetailChartContainer = ({location, ...props}) => {
  const {store} = useStore();
  const {server, session, devices, sinisterStore} = store;
  const theme = useContext(ThemeContext);
  const {type} = useParams();
  const intl = useIntl();

  const memoizedCategoryOptions = useRef();
  const isPrepared = useRef(false);
  const syncing = useRef(false);
  const chartTypeRef = useRef(null);
  const chartTypeGroupRef = useRef(null);
  const inputRef = useRef();
  const containerRef = useRef();
  const tableRef = useRef();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState();
  const [typeOption, setTypeOption] = useState();
  const [statusOption, setStatusOption] = useState();
  const [offset, setOffset] = useState();

  // Dentro do componente para acompanhar o idioma do IntlProvider; no módulo ficava fixo no do carregamento.
  const typeOptions = useMemo(() => [
    {id: 'device', title: intl.formatMessage({id: 'registeredDevices'})},
    {id: 'communication', title: intl.formatMessage({id: 'adminChartLastCommunication'})},
    {id: 'state', title: intl.formatMessage({id: 'adminChartConnectionStatus'})},
    {id: 'status', title: intl.formatMessage({id: 'adminChartDeviceConnection'})},
    {id: 'sinister', title: intl.formatMessage({id: 'adminChartDeviceIncidents'})},
  ], [intl]);

  const communicationOptions = useMemo(
      () => communicationLabelsArray.map((l, i) => ({
        id: communicationTime[i] || 'never',
        title: intl.formatMessage(l),
      })),
      [intl],
  );

  const sinisterOptions = useMemo(
      () => sinisterDurationLabelsArray.map((l, i) => ({
        id: sinisterGroupIds[i],
        title: intl.formatMessage(l),
      })),
      [intl],
  );

  const stateOptions = useMemo(
      () => statusLabelsArray.map((l, i) => ({
        id: stateGroupIds[i],
        title: intl.formatMessage(l),
      })),
      [intl],
  );

  const activeOptions = useMemo(
      () => activeLabelsArray.map((l, i) => ({
        id: getDeviceActiveGroupIdByIndex(i),
        title: intl.formatMessage(l),
      })),
      [intl],
  );

  const options = useMemo(() => {
    if (!(typeOption && devices.list.length)) return;
    switch (typeOption.id) {
      case 'device':
        return activeOptions;
      case 'communication':
        return communicationOptions;
      case 'state':
        return stateOptions;
      case 'sinister':
        return sinisterOptions;
      case 'status':
        memoizedCategoryOptions.current = getCurrentCategories(devices.list).reduce(
          (acc, cur) => [
            ...acc,
            ...statusLabelsArray.map((l, i) => ({
              id: `${cur}-${stateGroupIds[i]}`,
              title: `${intl.formatMessage({
                id: `category_${cur}`,
              })} - ${intl.formatMessage(l)}`,
            })),
          ],
          [],
        );
        return memoizedCategoryOptions.current;
      default:
        break;
    }
  }, [
    typeOption,
    devices.list,
    intl,
    communicationOptions,
    activeOptions,
    sinisterOptions,
    stateOptions,
  ]);

  const timezone = useMemo(
      () =>
          session.attributes.hasOwnProperty('timezone') ?
              session.attributes.timezone :
              server.attributes.hasOwnProperty('timezone') ?
                  server.attributes.timezone :
                  'America/Fortaleza',
      [],
  );

  // Mesma formatação usada no Cell e no cálculo de largura (getColumnWidth), para que a largura
  // das colunas de data acompanhe o texto exibido.
  const formatInstant = (value) => {
    if (!value) {
      return intl.formatMessage({id: 'notAvailable'});
    }
    return formatDateWithPattern(value, timezone, intl.formatMessage({id: 'dateTimeLongFormat'}));
  };
  const formatPositionDate = (currentPosition, field) => formatInstant(currentPosition?.[field]);

  const columns = useMemo(
      () => [
        {
          Header: '#',
          accessor: 'category',
          Cell: ({cell: {value}}) => createLabelIcon(value),
          width: 60,
        },
        {
          Header: intl.formatMessage({id: 'sharedName'}),
          accessor: 'name',
          minWidth: 60,
          width: getMeasuredColumnWidth(data, 'name', intl.formatMessage({id: 'sharedName'})),
        },
        {
          Header: intl.formatMessage({id: 'adminChartServerDate'}),
          accessor: (d) => d.currentPosition,
          Cell: ({cell: {value}}) => formatPositionDate(value, 'serverTime'),
          minWidth: 60,
          width: getMeasuredColumnWidth(
              data,
              ({currentPosition}) => currentPosition?.serverTime,
              intl.formatMessage({id: 'adminChartServerDate'}),
              formatInstant,
          ),
        },
        {
          Header: intl.formatMessage({id: 'adminChartDeviceDate'}),
          accessor: (d) => d.currentPosition,
          Cell: ({cell: {value}}) => formatPositionDate(value, 'deviceTime'),
          minWidth: 60,
          width: getMeasuredColumnWidth(
              data,
              ({currentPosition}) => currentPosition?.deviceTime,
              intl.formatMessage({id: 'adminChartDeviceDate'}),
              formatInstant,
          ),
        },
        {
          Header: intl.formatMessage({id: 'adminChartGpsDate'}),
          accessor: (d) => d.currentPosition,
          Cell: ({cell: {value}}) => formatPositionDate(value, 'fixTime'),
          minWidth: 60,
          width: getMeasuredColumnWidth(
              data,
              ({currentPosition}) => currentPosition?.fixTime,
              intl.formatMessage({id: 'adminChartGpsDate'}),
              formatInstant,
          ),
        },
        {
          Header: intl.formatMessage({id: 'sharedAddress'}),
          accessor: (d) => d.currentPosition,
          Cell: ({cell: {value}}) => (value ? value.address || '-' : '-'),
          minWidth: 60,
          width: getMeasuredColumnWidth(
              data,
              ({currentPosition}) =>
                  currentPosition ? currentPosition.address : '',
              intl.formatMessage({id: 'sharedAddress'}),
          ),
        },
      ],
      [data, intl],
  );

  useEffect(() => {
    setTypeOption(typeOptions.find((e) => e.id === type));
  }, [type, typeOptions]);

  useEffect(() => {
      const initialQuery = qs.parse(location.search, {
        ignoreQueryPrefix: true,
        comma: true,
      });

      // options derives from typeOption, which trails type by one render.
      if (options && typeOption?.id === type) {
        const selectedTypeOption = typeOptions.find((to) => to.id === type);
        const normalizedGroup = normalizeAdminChartGroup(
            type,
            initialQuery.group,
        );
        const selectedGroup =
            options.find((o) => String(o.id) === String(normalizedGroup)) ||
            options[0];
        // setValue emits onChange, so writing an unchanged value feeds the
        // effect its own dependencies back, and the handlers would navigate on
        // top of a URL the user never asked for.
        syncing.current = true;
        if (chartTypeRef.current?.getValue()?.id !== selectedTypeOption?.id) {
          chartTypeRef.current?.setValue(selectedTypeOption);
        }
        if (chartTypeGroupRef.current?.getValue()?.id !== selectedGroup?.id) {
          chartTypeGroupRef.current?.setValue(selectedGroup);
        }
        syncing.current = false;
        setStatusOption(selectedGroup);
        isPrepared.current = true;
      }
  }, [options, type, typeOption, typeOptions, location.search]);

  useEffect(() => {
    if (
      isPrepared.current &&
      options &&
      statusOption &&
      !options.some((option) => option.id === statusOption.id)
    ) {
      syncing.current = true;
      if (chartTypeGroupRef.current?.getValue()?.id !== options[0]?.id) {
        chartTypeGroupRef.current?.setValue(options[0]);
      }
      syncing.current = false;
      setStatusOption(options[0]);
    }
  }, [options, statusOption]);

  useEffect(() => {
    fromEvent(inputRef.current, 'input')
        .pipe(debounceTime(500))
        .subscribe((value) => {
          value = value.target.value;
          if (!value) setFiltered(null);
          else {
            setFiltered(
                data.filter(({name}) =>
                    String(name)
                        .toUpperCase()
                        .includes(String(value).toUpperCase()),
                ),
            );
          }
        });
  }, [data]);

  useEffect(() => {
    setLoading(true);
    if (devices.updatedPositions) {
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [devices.updatedPositions]);

  const headerTitle = useMemo(() => typeOptions.find((e) => e.id === type)?.title, [type, typeOptions]);

  const sortByLastUpdate = (data = []) => {
    return [...data].sort((a, b) => {
      // Turn your strings into dates, and then subtract them
      // to get a value that is either negative, positive, or zero.
      return new Date(b.lastUpdate) - new Date(a.lastUpdate);
    });
  };

  const sortByCreatedAt = (data = []) => {
    return [...data].sort((a, b) => {
      // Turn your strings into dates, and then subtract them
      // to get a value that is either negative, positive, or zero.
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  };

  useEffect(() => {
    // statusOption trails type by a render, so reading it before the sync effect
    // catches up would group by a key that belongs to the previous type.
    const groupMatchesType = options?.some((o) => o.id === statusOption?.id);
    if (statusOption && devices.list.length && typeOption?.id === type && groupMatchesType) {
      const source$ = from(devices.list).pipe(share());
      setLoading(true);
      switch (type) {
        case 'device':
          getDeviceActiveGroup(source$).subscribe((dateGroup) => {
            const groupKey = normalizeAdminChartGroup(type, statusOption.id);
            const data = sortByLastUpdate(dateGroup[groupKey]);
            setData(data);
            setLoading(false);
          });
          break;
        case 'communication':
          getDateGroup(source$, communicationTime).subscribe((dateGroup) => {
            const data = sortByLastUpdate(dateGroup[statusOption.id]);
            setData(data);
            setLoading(false);
          });
          break;
        case 'state':
          getStatusGroup(source$).subscribe((dateGroup) => {
            const data = sortByLastUpdate(
                dateGroup[String(statusOption.id).toLowerCase()],
            );
            setData(data);
            setLoading(false);
          });
          break;
        case 'status':
          const [category, status] = String(statusOption.id).split('-');
          getGroupByCategoryStatus(source$, category, status).subscribe(
              (dateGroup) => {
                setData(sortByLastUpdate(dateGroup));
                setLoading(false);
              },
          );
          break;
        case 'sinister':
          if (sinisterStore.incidents.length > 0 && devices.list.length) {
            getSinisterGroup(from(sinisterStore.incidents.filter((s) => s.status === 'opened')).pipe(share()))
                .subscribe((incidents) => {
                  const data = sortByCreatedAt(incidents[statusOption.id]);

                  const newDevices = data
                      .map((sinister) => devices.findById(sinister.deviceId))
                      .filter(Boolean);

                  setData(newDevices);
                  setLoading(false);
                });
          } else {
            setData([]);
            setLoading(false);
          }
          break;
        default:
          break;
      }
    }
  }, [type, typeOption, options, statusOption, devices.list, devices.updatedPositions,
    sinisterStore.incidents.length]);

  const selectItem = ({data}) => {
    if (['unknown', 'never'].includes(statusOption?.id)) return;
    history.push(`/admin/events/devices/${data.id}`, notificationClickState());
  };

  const changeType = (value) => {
    if (value !== null && !syncing.current) {
      history.push(`/admin/chart/${value.id}`);
    }
  };

  const changeOption = (value) => {
    if (value !== null && !syncing.current) {
      setStatusOption(value);
      if (!typeOption) return;
      // Written with the same library that reads it back: '+ de 72h' handed to
      // the URL raw returns as a space and matches no option, which snaps the
      // select to the first group.
      history.push(
          `/admin/chart/${typeOption.id}${buildGroupQuery(value.id)}`,
      );
    }
  };

  useEffect(() => {
    setOffset(getOffsetTo(tableRef.current, containerRef.current));
  }, [loading]);

  const getlistHeight = useCallback(() => {
    if (!containerRef.current) return 0;
    return containerRef.current.clientHeight - 32 - offset;
  }, [tableRef.current]);

  return (
      <Container fluid className="px-4 pb-4 position-relative h-100">
        <ListStyle
            ref={containerRef}
            theme={theme}
            className="custom-shadow-2 position-relative w-100 h-100"
            style={{bottom: 0}}
        >
          <Header className="mb-4">
            <h5>{headerTitle}</h5>
            <RoundButton className="mt-3 mb-3" tag={Link} to={'/admin'}>
              <span className="fas fa-times text-secondary"/>
            </RoundButton>
          </Header>

          <div className="d-flex">
            <ReactSelectStyled
                ref={chartTypeRef}
                radius="9px"
                scoped={false}
                options={typeOptions}
                name="chartType"
                noOptionsMessage={() => intl.formatMessage({id: 'sharedNoResultFound'})}
                placeholder={intl.formatMessage({id: 'adminChartChooseType'})}
                className="w-100 mb-3"
                onChange={changeType}
                isClearable={false}
                value={typeOption}
            />

            <div className="px-2"/>

            <ReactSelectStyled
                ref={chartTypeGroupRef}
                radius="9px"
                scoped={false}
                options={options}
                name="chartTypeGroup"
                noOptionsMessage={() => intl.formatMessage({id: 'sharedNoResultFound'})}
                placeholder={intl.formatMessage({id: 'adminChartChooseStatus'})}
                className="w-100 mb-3"
                onChange={changeOption}
                isClearable={false}
                value={statusOption}
            />
          </div>

          <div className="row py-4">
            <div style={{width: '90%', padding: 10}}>
              <InputSearchWithRef
                  placeholder={intl.formatMessage({id: 'adminChartFilterDevices'})}
                  inputRef={inputRef}
              />
            </div>
            <ExcelFile element={
              <RoundButton className="mt-3 mb-3 float-right"
                           style={{right: '-20px', top: '-12px', position: 'relative'}}>
                <span className="fas fa-download"/>
              </RoundButton>
            }>
              <ExcelSheet data={filtered || data} name={`${intl.formatMessage({id: 'deviceTitle'})}`}>
                <ExcelColumn label={`${intl.formatMessage({id: 'sharedName'})}`} value="name"/>
                <ExcelColumn label={`${intl.formatMessage({id: 'sharedDate'})}`}
                             value={(col) => !col.lastUpdate ?
                                 intl.formatMessage({id: 'notAvailable'}) :
                                 momentTimezone(col.lastUpdate)
                                     .tz(timezone)
                                     .format(intl.formatMessage({id: 'dateTimeLongFormat'}))}/>

                <ExcelColumn label={`${intl.formatMessage({id: 'positionAddress'})}`}
                             value={(col) => col.currentPosition ? (col.currentPosition.address || '-') : '-'}/>
              </ExcelSheet>
            </ExcelFile>
          </div>

          {loading ? (
              <LoadingBar/>
          ) : (
              <ChartTableThemedHeader ref={tableRef}>
                <ReactTable
                    data={filtered || data}
                    columns={columns}
                    selectItem={selectItem}
                    listHeight={getlistHeight()}
                />
              </ChartTableThemedHeader>
          )}
        </ListStyle>
      </Container>
  );
};

export default observer(AdminDetailChartContainer);
